import { db } from '../../firebase/firebaseConfig';
import { collection, query, where, getDocs, updateDoc, doc, addDoc, orderBy } from 'firebase/firestore';
import { COLLECTIONS } from '../../constants/collections';
import { VEHICLE_STATUS } from '../../constants/statuses';

// Get reservations specifically for a transporter company
export const getTransporterReservations = async (transporterId, status = 'all', tenantId, startDate = null, endDate = null) => {
    try {
        if (!tenantId) throw new Error('Tenant ID is required');
        const reservationsRef = collection(db, `tenants/${tenantId}/${COLLECTIONS.RESERVATION_SLOTS || 'reservationSlots'}`);

        // Simplified query to avoid composite index requirements
        // Filtering will be mainly done in-memory for better robustness
        const q = query(
            reservationsRef,
            where('transporterId', '==', transporterId)
        );

        const snapshot = await getDocs(q);
        let results = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            startTime: doc.data().startTime?.toDate(),
            endTime: doc.data().endTime?.toDate()
        }));

        // In-memory filtering for robustness (avoiding Firebase index errors)

        // 1. Status Filter
        if (status === 'pending') {
            const pendingStatuses = ['pending', 'pending_approval', 'pending_transporter_confirmation'];
            results = results.filter(r => pendingStatuses.includes(r.status));
        } else if (status !== 'all') {
            results = results.filter(r => r.status === status);
        }

        // 2. DateRange Filter
        if (startDate || endDate) {
            const startLimit = startDate ? new Date(startDate) : null;
            if (startLimit) startLimit.setHours(0, 0, 0, 0);

            const endLimit = endDate ? new Date(endDate) : null;
            if (endLimit) endLimit.setHours(23, 59, 59, 999);

            results = results.filter(r => {
                if (!r.startTime) return false;
                const rDate = new Date(r.startTime);
                if (startLimit && rDate < startLimit) return false;
                if (endLimit && rDate > endLimit) return false;
                return true;
            });
        }

        // Manual Sort
        return results.sort((a, b) => (b.startTime || 0) - (a.startTime || 0));
    } catch (error) {
        console.error('Error fetching transporter reservations:', error);
        throw error;
    }
};

// Update status (Approve/Reject)
export const updateTransporterReservationStatus = async (reservationId, status, userId, notes = '', tenantId) => {
    try {
        if (!tenantId) throw new Error('Tenant ID is required');
        const resRef = doc(db, `tenants/${tenantId}/${COLLECTIONS.RESERVATION_SLOTS || 'reservationSlots'}`, reservationId);

        // Map 'confirmed' input to standard APPROVED status
        const finalStatus = status === 'confirmed' ? VEHICLE_STATUS.APPROVED : status;

        const updateData = {
            status: finalStatus,
            updatedBy: userId,
            updatedAt: new Date(),
            transporterNotes: notes
        };

        if (finalStatus === VEHICLE_STATUS.APPROVED) {
            updateData.confirmedAt = new Date();
        }

        await updateDoc(resRef, updateData);

        return true;
    } catch (error) {
        console.error('Error updating reservation status:', error);
        throw error;
    }
};

// Request a revision (propose new time)
export const requestSlotRevision = async (reservationId, proposedTime, reason, userId, tenantId) => {
    try {
        if (!tenantId) throw new Error('Tenant ID is required');
        const resRef = doc(db, `tenants/${tenantId}/${COLLECTIONS.RESERVATION_SLOTS || 'reservationSlots'}`, reservationId);

        await updateDoc(resRef, {
            status: 'revision_requested',
            revisionRequest: {
                proposedTime: proposedTime,
                reason: reason,
                requestedBy: userId,
                requestedAt: new Date()
            },
            updatedBy: userId,
            updatedAt: new Date()
        });
        return true;
    } catch (error) {
        console.error('Error requesting revision:', error);
        throw error;
    }
};
